<template>
	<div
		:class="[
			'tabs',
			variant === 'vertical' && 'tabs--vertical',
			variant === 'horizontalWhite' && 'tabs--horizontal-white'
		]"
	>
		<div
			class="tabs__header"
			data-test="tabsHeaderContainer"
			ref="tabAreaRef"
		>
			<Button
				v-show="displayPageBack && !isVertical"
				className="tabs__previus-btn btn--icon-only"
				iconClassName="icon-arrow-left"
				:iconBeforeLabel="true"
				:btnClick="pageBack"
			/>
			<ul
				class="tabs__header-list"
				ref="stepListRef"
				:style="{
					height: isVertical && height
				}"
				data-test="tabsHeader"
			>
				<li
					@click="clickTab(index)"
					:class="[
						'tabs__header-item',
						i.active && 'is-active',
						i.disabled && 'is-disabled'
					]"
					v-for="(i, index) in localSteps"
					:key="index"
					data-test="tab-item"
					:tabindex="i.disabled ? -1 : 0"
				>
					<slot :name="index + 1">
						{{ i.title ? i.title : '' }}
					</slot>
				</li>
			</ul>
			<!-- v-show="nextActive" -->
			<Button
				v-show="displayPageForward && !isVertical"
				:btnClick="pageForward"
				className="tabs__next-btn btn--icon-only"
				iconClassName="icon-arrow-right"
				:iconBeforeLabel="true"
			/>
			<div class="tabs__vertical-btn-container" v-if="isVertical">
				<Button
					v-show="displayPageBack"
					className="tabs__up-btn btn--icon-only"
					iconClassName="icon-arrow-up"
					:iconBeforeLabel="true"
					:btnClick="pageBack"
				/>
				<Button
					v-show="displayPageForward"
					className="tabs__down-btn btn--icon-only"
					iconClassName="icon-arrow-down"
					:iconBeforeLabel="true"
					:btnClick="pageForward"
				/>
			</div>
		</div>

		<div
			v-for="(i, index) in localSteps"
			class="tabs__content"
			data-test="tab-content"
			:key="index"
			v-show="currentIndexActive === index"
		>
			<slot :name="'content' + (index + 1)" />
		</div>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import scrollNavLogic from '@/mixins/scroll-nav-logic';

export default {
	name: 'Tabs',
	mixins: [scrollNavLogic],
	components: {
		Button
	},
	props: {
		tabs: {
			type: Array,
			default: () => [],
			validator: value => value.filter(item => item.active).length <= 1
		},
		variant: {
			type: String,
			default: '',
			validator: value =>
				['', 'vertical', 'horizontalWhite'].indexOf(value) !== -1
		},
		height: {
			type: String,
			default: '200px'
		}
	},
	data() {
		return {
			localSteps: [...this.tabs],
			containerWidth: 0,
			containerWidthAllItems: 0,
			currentTranslateX: 0,
			step: 0
		};
	},
	mounted() {
		this.setContainerWidth();
		window.addEventListener('resize', this.setContainerWidth);

		this.isMounted = true;
	},
	created() {
		let tabIndexActive = this.tabs.findIndex(item => item.active);
		//make sure 1 is active
		if (tabIndexActive < 0) {
			tabIndexActive = 0;
		}
		this.currentIndexActive = tabIndexActive;
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.setContainerWidth);
	},
	computed: {
		/*NEXT BUTTON*/
		nextIndexTabVisible() {
			return this.lastIndexTabVisible + this.totalTabVisible;
		},
		canContinueNextTab() {
			return this.nextIndexTabVisible < this.tabsCount;
		},
		displayPageForward() {
			return this.canContinueNextTab;
		},
		/*PREVIOUS BUTTOM */
		previousIndexTabVisible() {
			return this.lastIndexTabVisible - this.totalTabVisible;
		},
		canBackToPreviousTab() {
			return this.previousIndexTabVisible >= 0;
		},
		displayPageBack() {
			return this.currentTranslateX < 0;
		},
		totalTabVisible() {
			return Math.floor(this.containerWidth / this.tabWidth);
		},
		isVertical() {
			return this.variant === 'vertical';
		},
		tabWidth() {
			if (this.isMounted) {
				this.setContainerWidth();
			}

			const width = Math.floor(
				this.containerWidthAllItems / this.tabsCount
			);

			return width;
		},
		tabsCount() {
			let count = 0;

			return this.localSteps.length || count;
		},
		allTabsVisible() {
			let result = false;

			const allTabsWidth = this.tabsCount * this.tabWidth;

			if (allTabsWidth <= this.containerWidth) {
				result = true;
			}

			return result;
		}
	},
	methods: {
		setStep() {
			const paddingOffset = 8;
			let result = 0;

			result = this.totalTabVisible * this.tabWidth + paddingOffset;

			this.step = result;
		},
		setContainerWidth() {
			this.containerWidth = this.$refs.tabAreaRef.offsetWidth; //visible width
			this.containerWidthAllItems = this.$refs.stepListRef.scrollWidth; //width of all items including overflow
		},
		clickTab(index) {
			this.updateCurrentIndexActiveWithoutScroll(index);
		},
		pageForward() {
			if (!this.step) {
				this.setStep();
			}
			if (this.canContinueNextTab) {
				this.transformContainer(-1 * this.step);
				this.lastIndexTabVisible = this.nextIndexTabVisible;
				this.updateCurrentScroll(this.lastIndexTabVisible);
			}
		},
		pageBack() {
			if (this.currentTranslateX < 0) {
				this.transformContainer(this.step);
			}

			if (this.canBackToPreviousTab) {
				this.lastIndexTabVisible = this.previousIndexTabVisible;
				this.updateCurrentScroll(this.lastIndexTabVisible);
			}
		},
		transformContainer(step) {
			this.currentTranslateX = this.currentTranslateX + step;
			this.containerStyles = {
				transform: `translateX(${this.currentTranslateX}px)`
			};
		}
	}
};
</script>

<style lang="scss" scoped>
.tabs {
	display: flex;
	flex-direction: column;
	width: 100%;

	&__header {
		position: relative;
		display: flex;
		align-items: center;
		padding: $padding-sm auto;

		& .btn--icon-only {
			width: 48px;
			height: 48px;
			font-size: 24px;
		}
	}

	&__header-list {
		display: flex;
		overflow: hidden;
	}

	&__header-item {
		cursor: pointer;
		white-space: nowrap;
		padding: $padding-sm 40px;
		border-bottom: 2px solid $neutral-4;
		min-width: 112px;
		position: relative;
		@include body-text('regular');
		max-width: 250px;
		@include ellipsis;

		&:hover {
			background: $white;
		}

		&:focus {
			&::after {
				content: '';
				border: 2px solid $base;
				@include vertical-horizontal-center-with-transform;
				width: 100%;
				height: 100%;
				pointer-events: none;
				z-index: 10;
			}
		}

		&:focus-visible {
			outline: none;
		}

		&.is-active {
			@include body-text('regular-emphasis');

			&:focus {
				&::after {
					border: none;
				}
			}

			&::before {
				content: '';
				border-bottom: 4px solid $base;
				@include vertical-horizontal-center-with-transform;
				width: 100%;
				height: 100%;
				pointer-events: none;
				z-index: 10;
			}
		}
	}

	&__content {
		padding: 0 $padding-lg $padding-md $padding-lg;
	}

	&__previus-btn {
		margin-right: $gutter-md;
	}

	&__next-btn {
		margin-left: $gutter-md;
	}

	::v-deep .btn--icon-only .icon {
		padding: $padding-sm;
	}

	&--vertical {
		display: grid;
		grid-template-columns: auto 1fr;

		.tabs {
			&__header {
				flex-wrap: wrap;
				width: 174px;
			}

			&__header-list {
				display: block;
				width: 100%;
			}

			&__header-item {
				border-bottom: none;
				border-right: 2px solid $neutral-4;
				padding: $padding-sm;

				&:hover {
					background: $ui-positive-light;
				}

				&.is-active {
					background: $ui-positive-light;

					&::before {
						border-right: 4px solid $base;
						border-bottom: none;
						top: 0;
						transform: translate(-50%, 0%);
					}
				}

				&:first-child {
					margin-left: 0;
				}

				&:last-child {
					margin-right: 0;
				}
			}

			&__vertical-btn-container {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				width: 100%;
				gap: $gutter-md;

				& > :last-child {
					margin-right: $gutter-lg;
				}
			}
		}
	}

	&--horizontal-white {
		.tabs {
			&__header-item:hover {
				background: $accent;
			}
		}
	}

	&::v-deep .btn--icon-only:focus .icon {
		outline: none;
		&::after {
			content: '';
			border: 2px solid $base;
			@include vertical-horizontal-center-with-transform;
			width: calc(100% + 2px);
			height: calc(100% + 2px);
			pointer-events: none;
		}
	}
}
</style>
